// @flow

import React from 'react';
import { matchPath, Redirect, Route, Switch } from 'react-router-dom';

import { AUTH_ROUTE, NON_AUTH_ROUTE } from './constants';
import { resolveRouteData } from './helpers';
import type { RouteRendererProps } from './types';

/**
 * @description
 * This is a wrapper component on top of React Router which renders the routing
 * of an application with a given routing schema.
 *
 * @param {RouteRendererProps} props
 * @param {boolean} props.isSecureEnabled Whether the consumed application is in secure mode
 * @param {LocationShape} props.location Router location
 * @param {RouteConfig} props.routeConfig Route configuration object of the application
 * @param {Store} props.store Redux store passed from the application
 *
 * @example
 * --> in routes.js
 * const routeConfig = {
 *   redirectIfNotFound: true,  // Redirect if route is not found. Depending on secure mode, it will redirect to default unsecure or secure route.
 *   secureDefaultRoute: '/secure', // Default secure route.
 *   unsecureDefaultRoute: '/unsecure', // Default unsecure route.
 *   routes: [
 *      {
 *        path: '/unsecure',
 *        component: Unsecure,
 *        exact: true,
 *        type: 'NON_AUTH_ROUTE',
 *      },
 *      {
 *        path: '/secure',
 *        component: Secure,
 *        exact: true,
 *        type: 'AUTH_ROUTE',
 *      },
 *      {
 *        path: '/parent',
 *        component: Parent,
 *        exact: true,
 *        type: 'PUBLIC_ROUTE',
 *        routes: [
 *          {
 *            path: '/parent/secure',
 *            component: ParentChildSecure,
 *            exact: true,
 *            type: 'AUTH_ROUTE',
 *          },
 *          {
 *            path: '/parent/public',
 *            component: ParentChildPublic,
 *            exact: true,
 *            type: 'PUBLIC_ROUTE',
 *          },
 *          {
 *            path: '/parent/something',
 *            redirectTo: '/parent/public',
 *          },
 *        ]
 *      },
 *      {
 *        path: '/abcd',
 *        redirectTo: '/parent',
 *      },
 *   ]
 * };
 *
 * --> App.js
 * import { BrowserRouter as Router } from 'react-router-dom';
 * import { RouterRender } from '@fil-global/eiswagen';
 *
 * class App extends Component {
 *  render() {
 *    const { flagToCheckIfAppIsInSecureMode, location, store } = this.props;
 *    return (
 *      <Provider store={store}>
 *        <Router>
 *          <RouterRender
 *            isSecureEnabled={flagToCheckIfAppIsInSecureMode}
 *            location={location}
 *            routeConfig={routeConfig}
 *            store={store}
 *          />
 *        </Router>
 *      </Provider>
 *    );
 *  }
 * }
 */
export default ({ isSecureEnabled, location, routeConfig, store }: RouteRendererProps) => {
  const {
    fromParent,
    redirectIfNotFound,
    routes,
    secureDefaultRoute,
    unsecureDefaultRoute,
  } = routeConfig;

  if (!Array.isArray(routes) || !routes.length) {
    return null;
  }

  /**
   * @description
   * Wrapper function for rendering the Route.
   */
  const RouteWithSubRoutes = (route, i) => {
    const { component: Component, routeData } = route;
    let componentData = {};

    // $FlowFixMe eslint-disable-line
    if (location && matchPath(location.pathname, route) && routeData) {
      componentData = resolveRouteData({ routeData, store });
    }

    return (
      <Route
        path={route.path}
        exact={route.exact}
        key={i.toString()}
        render={props => {
          // Redirect if redirectTo specified.
          if (route.redirectTo) {
            return <Redirect to={route.redirectTo} />;
          }

          // Redirect to default secure route, If
          // 1. route is NON_AUTH_ROUTE
          // 2. application is in secure mode
          if (route.type === NON_AUTH_ROUTE && isSecureEnabled && secureDefaultRoute) {
            return <Redirect to={secureDefaultRoute} />;
          }

          // Redirect to default unsecure route, If
          // 1. route is AUTH_ROUTE
          // 2. application is not in secure mode
          if (route.type === AUTH_ROUTE && !isSecureEnabled && unsecureDefaultRoute) {
            return <Redirect to={unsecureDefaultRoute} />;
          }

          return (
            <Component
              key={location.key}
              {...props}
              routeConfig={{
                fromParent: route.routes && route.routes.length > 0,
                redirectIfNotFound,
                // $FlowFixMe eslint-disable-line
                routes: route.routes,
                secureDefaultRoute,
                unsecureDefaultRoute,
              }}
              {...componentData}
            />
          );
        }}
      />
    );
  };

  /**
   * @description
   * Wrapper function to handle default route redirect if 404.
   */
  const NotFoundRedirect = () => {
    // We will not render this if redirectIfNotFound is false or
    // rendering is part of sub routing.
    if (fromParent || !redirectIfNotFound) {
      return null;
    }

    // Depending on application secure mode, render redirect accordingly.
    if (isSecureEnabled && secureDefaultRoute) {
      return <Redirect to={secureDefaultRoute} />;
    }
    if (!isSecureEnabled && unsecureDefaultRoute) {
      return <Redirect to={unsecureDefaultRoute} />;
    }
    return null;
  };

  return (
    <Switch>
      {routes.map((route, i) =>
        // $FlowFixMe eslint-disable-line
        RouteWithSubRoutes(route, i)
      )}
      {NotFoundRedirect()}
    </Switch>
  );
};
